import type { CSSProperties, FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import {
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import type { EncryptionKey } from '@/infra/crypto/key-ring'

interface KeyRingTableProps {
  readonly keys: readonly EncryptionKey[]
  readonly activeKeyVersion: number | null
}

export const KeyRingTable: FC<KeyRingTableProps> = ({ keys, activeKeyVersion }) => {
  if (keys.length === 0) {
    return (
      <Typography variant="body2" sx={emptyMessageSx}>
        No encryption keys configured.
      </Typography>
    )
  }

  return (
    <TableContainer>
      <Table size="small" aria-label="Encryption key versions">
        <caption style={visuallyHiddenStyle}>
          Encryption key versions showing version number, creation date, and current status
        </caption>
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>Version</TableCell>
            <TableCell sx={headerCellSx}>Created</TableCell>
            <TableCell sx={headerCellSx} align="right">
              Status
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {keys.map((encryptionKey) => (
            <TableRow key={encryptionKey.version} sx={rowSx}>
              <TableCell sx={cellSx}>v{encryptionKey.version}</TableCell>
              <TableCell sx={cellSx}>{formatCreatedDate(encryptionKey.createdAt)}</TableCell>
              <TableCell sx={cellSx} align="right">
                <Chip
                  label={encryptionKey.version === activeKeyVersion ? 'Active' : 'Retired'}
                  size="small"
                  color={encryptionKey.version === activeKeyVersion ? 'success' : 'default'}
                  variant="outlined"
                  sx={chipSx}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function formatCreatedDate(isoDate: string): string {
  try {
    return new Date(isoDate).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return isoDate
  }
}

const emptyMessageSx: SxProps<Theme> = {
  color: 'text.disabled',
  fontSize: '0.8rem',
  py: 2,
}

const headerCellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.75rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottomColor: 'divider',
  py: 1,
}

const cellSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.8rem',
  borderBottomColor: 'divider',
  py: 1,
}

const rowSx: SxProps<Theme> = {
  '&:last-child td': {
    borderBottom: 0,
  },
}

const chipSx: SxProps<Theme> = {
  height: 22,
  fontSize: '0.7rem',
}

const visuallyHiddenStyle: CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  borderWidth: 0,
}
